import { currencySchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated } from './common/pagination.js';
import { queryBooleanSchema } from './common/query.js';
import { generatedFileSchema } from './generated-file.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

/**
 * The payment states a row of the accrual list can be in: what the donation's payment
 * axis says about money that is owed. A cancelled donation, a `not_charged` one
 * and a `refunded` one owe nothing and are not rows (docs/decisions/0142 §2).
 */
export const ACCRUAL_STATUSES = ['unpaid', 'partially_settled', 'settled', 'reversed'] as const;

export type AccrualStatus = (typeof ACCRUAL_STATUSES)[number];

export const accrualStatusSchema = z.enum(ACCRUAL_STATUSES);

export const ACCRUAL_SORT_KEYS = [
  'donatedAt',
  'referenceCode',
  'donor',
  'reference',
  'recordedBy',
  'campaign',
  'owed',
  'collected',
  'remaining',
  'status',
] as const;

export type AccrualSortKey = (typeof ACCRUAL_SORT_KEYS)[number];

/**
 * The filter of the accrual list, and so of its file. Every field narrows
 * the donations the caller's scope already reaches; none widens it.
 *
 * - `search` is the donor's name, the reference's name, the donation's
 *   reference code or the recording user's displayed name;
 * - `status` is the donation's payment status;
 * - `owing` is *remaining > 0*: the donations that can still take a match, the
 *   predicate the donation list's `owing` filter and a donor's outstanding use;
 * - `from` and `to` bound the donation date by the reporting day, as the
 *   report's window does.
 */
export const accrualFilterSchema = z.object({
  search: z.string().trim().max(200).optional(),
  status: accrualStatusSchema.optional(),
  recordedByUserId: z.uuid().optional(),
  campaignId: z.uuid().optional(),
  campaignPeriodId: z.uuid().optional(),
  currency: currencySchema.optional(),
  from: isoDaySchema.optional(),
  to: isoDaySchema.optional(),
  owing: queryBooleanSchema.optional(),
});

export type AccrualFilter = z.infer<typeof accrualFilterSchema>;

const windowIsOrdered = (input: { from?: string | undefined; to?: string | undefined }) =>
  input.from === undefined || input.to === undefined || input.from <= input.to;

export const accrualListInputSchema = sortedListRequest(ACCRUAL_SORT_KEYS, 'donatedAt', 'desc')
  .extend(accrualFilterSchema.omit({ search: true }).shape)
  .refine(windowIsOrdered, 'the period ends after it starts');

export type AccrualListInput = z.infer<typeof accrualListInputSchema>;

/**
 * What a generated file is asked for: the screen's filter and its ordering,
 * with no page. The file is every row of it.
 */
export const accrualExportInputSchema = z
  .object({
    ...accrualFilterSchema.shape,
    sortBy: z.enum(ACCRUAL_SORT_KEYS).default('donatedAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
  })
  .refine(windowIsOrdered, 'the period ends after it starts');

export type AccrualExportInput = z.infer<typeof accrualExportInputSchema>;

/**
 * One donation. `owed` is the donation's amount, `collected` the donation side
 * of its confirmed matches and `remaining` the first less the second, all in the
 * donation's own currency — a row never mixes money and nothing is summed across
 * currencies. A negative `remaining` is over-collection, shown as it is.
 *
 * `donor` is the payer; an anonymous donor's name is withheld here, on the
 * server. `referenceName` is the person who introduced the donor. `recordedBy` is the operator the donation is recorded for
 * (`donations.created_by_user_id`), chosen through the display helper.
 */
export const accrualRowSchema = z.object({
  id: z.uuid(),
  referenceCode: z.string(),
  donatedAt: z.iso.datetime({ offset: true }),
  donor: z.object({
    id: z.uuid().nullable(),
    name: z.string().nullable(),
    isAnonymous: z.boolean(),
  }),
  referenceName: z.string().nullable(),
  recordedBy: z.object({ id: z.uuid().nullable(), name: z.string().nullable() }),
  campaign: z.object({ id: z.uuid(), name: z.string() }),
  seasonYear: z.number().int().nullable(),
  owed: moneySchema,
  collected: moneySchema,
  remaining: moneySchema,
  paymentStatus: accrualStatusSchema,
});

export type AccrualRow = z.infer<typeof accrualRowSchema>;

/** How many rows each status holds under every filter but the status itself — the tabs' counts. */
export const accrualStatusCountsSchema = z.object({
  unpaid: z.number().int().min(0),
  partially_settled: z.number().int().min(0),
  settled: z.number().int().min(0),
  reversed: z.number().int().min(0),
});

export type AccrualStatusCounts = z.infer<typeof accrualStatusCountsSchema>;

export const accrualListSchema = paginated(accrualRowSchema).extend({
  statusCounts: accrualStatusCountsSchema,
});

export type AccrualList = z.infer<typeof accrualListSchema>;

export const ACCRUAL_RECORDER_OPTIONS_LIMIT = 25;

export const accrualRecordersInputSchema = z
  .object({
    ...accrualFilterSchema.omit({ search: true, recordedByUserId: true }).shape,
    search: z.string().trim().max(100).optional(),
  })
  .refine(windowIsOrdered, 'the period ends after it starts');

export type AccrualRecordersInput = z.infer<typeof accrualRecordersInputSchema>;

/**
 * The users who recorded donations in the caller's scope under the rest of the
 * filter, by displayed name — what the recording-user filter offers, so it names
 * nobody a row of the list does not.
 */
export const accrualRecordersSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), name: z.string(), donationCount: z.number().int() })),
});

export type AccrualRecorders = z.infer<typeof accrualRecordersSchema>;

export const accrualContract = {
  list: oc
    .route({ method: 'GET', path: '/accruals' })
    .errors(guarded)
    .input(accrualListInputSchema)
    .output(accrualListSchema),

  recorders: oc
    .route({ method: 'GET', path: '/accruals/recorders' })
    .errors(guarded)
    .input(accrualRecordersInputSchema)
    .output(accrualRecordersSchema),

  export: oc
    .route({ method: 'POST', path: '/accruals/export' })
    .errors(guarded)
    .input(accrualExportInputSchema)
    .output(generatedFileSchema),
};
